const MAX_PHOTO_EDGE = 2048;
const JPEG_QUALITY = 0.82;

export const getPhotoContentType = (file: Pick<File, 'name' | 'type'>): string | null => {
    const type = file.type.toLowerCase();
    if (['image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/heif'].includes(type)) {
        return type;
    }
    if (type && type !== 'application/octet-stream' && type !== 'binary/octet-stream') {
        return null;
    }
    const extension = file.name.split('.').pop()?.toLowerCase();
    switch (extension) {
        case 'jpg':
        case 'jpeg':
            return 'image/jpeg';
        case 'png':
            return 'image/png';
        case 'heic':
        case 'heif':
            return `image/${extension}`;
        default:
            return null;
    }
};

export const resizePhoto = async (file: File): Promise<Blob | null> => {
    if (typeof createImageBitmap !== 'function' || typeof OffscreenCanvas !== 'function') {
        return null;
    }
    const contentType = getPhotoContentType(file);
    if (!contentType) {
        return null;
    }
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    try {
        const scale = Math.min(1, MAX_PHOTO_EDGE / Math.max(bitmap.width, bitmap.height));
        if (scale === 1) {
            return null;
        }
        const canvas = new OffscreenCanvas(
            Math.max(1, Math.round(bitmap.width * scale)),
            Math.max(1, Math.round(bitmap.height * scale)),
        );
        const context = canvas.getContext('2d');
        if (!context) {
            return null;
        }
        context.imageSmoothingEnabled = true;
        context.imageSmoothingQuality = 'high';
        context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
        // Keep PNG transparency; other supported photo formats become JPEG.
        const outputType = contentType === 'image/png' ? 'image/png' : 'image/jpeg';
        const blob = await canvas.convertToBlob({ type: outputType, quality: JPEG_QUALITY });
        return blob.type === outputType && blob.size > 0 && blob.size < file.size ? blob : null;
    } finally {
        bitmap.close();
    }
};
